import {
  Turnstile,
  type TurnstileInstance,
} from "@marsidev/react-turnstile";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, BadgePercent, Check, Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { z } from "zod";

import { lookupPartnerCode } from "@/services/partnerLookupService";
import { setPendingPartnerCode } from "@/lib/pendingPartnerCode";

import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { AudienceToggle } from "@/components/auth/AudienceToggle";
import { AuthBrandColumn } from "@/components/auth/AuthBrandColumn";
import { AuthBrandMark } from "@/components/auth/AuthBrandMark";
import { AuthStatusScreen } from "@/components/auth/AuthStatusScreen";
import { GoogleSsoButton } from "@/components/auth/GoogleSsoButton";
import {
  getLandingPrivacyUrl,
  getLandingTermsUrl,
} from "@/lib/landingLinks";
import { isLocalDevelopmentHost } from "@/lib/isLocalDevelopmentHost";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  sendVerificationEmail as sendVerificationEmailApi,
  verifyEmail as verifyEmailApi,
} from "@/services/authService";
import { translateAuthErrorMessage } from "@/utils/translateAuthErrorMessage";
import { useFunnelProgress } from "@/lib/analytics/funnel";
import { formErrorsTracker } from "@/lib/analytics/friction";
import { trackRegisterStarted } from "@/lib/mag";

type Step = 1 | 2;
type AccountType = "business" | "accountant";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ?? "";

interface PasswordRules {
  len: boolean;
  case: boolean;
  num: boolean;
  spec: boolean;
}

function evaluateRules(password: string): PasswordRules {
  return {
    len: password.length >= 8,
    case: /[a-z]/.test(password) && /[A-Z]/.test(password),
    num: /\d/.test(password),
    spec: /[^A-Za-z0-9]/.test(password),
  };
}

export function RegisterPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, registerAndLogin } = useAuth();
  const turnstileEnabled =
    TURNSTILE_SITE_KEY !== "" && !isLocalDevelopmentHost();
  const [step, setStep] = useState<Step>(1);

  // Signup drop-off, same reasoning as onboarding: an abandoned registration
  // is a lost customer, and it is invisible server-side because step 1 never
  // reaches the API.
  useFunnelProgress("registration", step);

  // Top of the Magig funnel; signup_completed is reported by the backend.
  // Ref-guarded because React 18 StrictMode mounts effects twice in dev,
  // which would double-count every local page open.
  const registerStartedRef = useRef(false);
  useEffect(() => {
    if (registerStartedRef.current) return;
    registerStartedRef.current = true;
    trackRegisterStarted();
  }, []);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [searchParams] = useSearchParams();
  const initialAudience: AccountType =
    searchParams.get("audience") === "contador" ? "accountant" : "business";
  const [accountType, setAccountType] = useState<AccountType>(initialAudience);
  const urlPartnerCode = searchParams.get("partner")?.trim() || "";

  // Manual entry: a partner may just tell members "use code AJE2026"
  // without a special link, so the URL param alone isn't enough. Looked up
  // on BLUR, not while typing — the endpoint is deliberately throttled to
  // 10/15min (see PartnerLookupController), and a debounce-while-typing
  // approach still fires one request per pause (natural pauses, typos,
  // corrections), which is enough to burn that budget before the user
  // finishes a single code. Committing only on blur means at most one
  // request per code the user actually settles on.
  const [manualPartnerCode, setManualPartnerCode] = useState("");
  const [showPartnerCodeInput, setShowPartnerCodeInput] = useState(false);
  const [committedManualCode, setCommittedManualCode] = useState("");
  // Bumped on every blur (even re-blurring the same value) so a failed
  // lookup can be retried without editing the code — react-query won't
  // refetch an unchanged key under staleTime: Infinity otherwise.
  const [lookupAttempt, setLookupAttempt] = useState(0);

  const commitManualCode = (): void => {
    const trimmed = manualPartnerCode.trim();
    setCommittedManualCode(trimmed.length >= 3 ? trimmed : "");
    setLookupAttempt((n) => n + 1);
  };

  // URL codes are used as-is (already complete, not typed live); manual
  // codes only commit past a trivial length so an accidental one-character
  // blur doesn't spend a lookup.
  const partnerCode = urlPartnerCode || committedManualCode;

  // Bridges to onboarding: a Google signup (or an email/password signup
  // without a RUC yet) creates no Company here — that happens later, in a
  // separate onboarding step, which is too far away for React state to
  // reach. sessionStorage survives both the full-page OAuth redirect and
  // the gap until then (see pendingPartnerCode.ts). Persisted from the
  // LIVE input, not the committed/looked-up value, so it's already there
  // the instant "Registrate con Google" is clicked, even if the field
  // never blurred first.
  useEffect(() => {
    setPendingPartnerCode(urlPartnerCode || manualPartnerCode.trim());
  }, [urlPartnerCode, manualPartnerCode]);

  // UX-only: drives the benefit banner + inline validity message. NEVER
  // gates what gets submitted — see submitPartnerCode below, which sends
  // whatever the user actually typed regardless of whether this lookup
  // completed, since /auth/register re-validates authoritatively anyway.
  // Gating submission on this would silently drop a valid code any time
  // the lookup hadn't resolved yet (e.g. Enter pressed before blur fires).
  const {
    data: partnerLookup,
    isFetching: partnerLookupLoading,
    isError: partnerLookupErrored,
  } = useQuery({
    queryKey: ["partner-lookup", partnerCode, urlPartnerCode ? 0 : lookupAttempt],
    queryFn: () => lookupPartnerCode(partnerCode),
    enabled: partnerCode !== "",
    retry: false,
    staleTime: Infinity,
  });

  const formSchema = z
    .object({
      email: z
        .string()
        .min(1, t("validation.emailRequired"))
        .email(t("validation.emailInvalid")),
      password: z
        .string()
        .min(8, t("auth.register.passwordStrength.minLength")),
      confirmPassword: z.string().min(1, t("auth.register.confirmPasswordRequired")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("auth.register.passwordMismatch"),
      path: ["confirmPassword"],
    });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const pwdValue = form.watch("password") ?? "";
  const rules = evaluateRules(pwdValue);

  const handleSendCode = async (): Promise<void> => {
    const values = form.getValues();
    if (turnstileEnabled && !turnstileToken?.trim()) {
      toast.error(t("auth.register.captchaRequired"));
      return;
    }
    setEmail(values.email);
    setPassword(values.password);
    setSendingCode(true);
    try {
      await sendVerificationEmailApi(
        values.email,
        i18n.language,
        turnstileToken ?? undefined,
      );
      setStep(2);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { message?: string | string[] } };
      };
      const status = axiosErr.response?.status;
      const rawMsg = axiosErr.response?.data?.message;
      const msg = Array.isArray(rawMsg) ? rawMsg[0] : rawMsg;
      if (status === 409) {
        toast.error(t("auth.register.emailAlreadyRegisteredError"));
      } else if (status === 400) {
        const translated =
          translateAuthErrorMessage(msg, t) ?? t("auth.register.error");
        toast.error(translated);
        if (
          msg === "Captcha verification failed" ||
          msg === "Captcha verification is required"
        ) {
          turnstileRef.current?.reset();
          setTurnstileToken(null);
        }
      } else {
        toast.error(t("auth.register.error"));
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyAndRegister = async (): Promise<void> => {
    const code = verificationCode.trim();
    if (code.length !== 6) {
      toast.error(t("auth.register.invalidCodeError"));
      return;
    }
    setVerifying(true);
    try {
      const { registrationToken } = await verifyEmailApi(email, code);
      // Send whatever code the user actually provided — via the URL or
      // typed manually — regardless of whether the UX-only lookup above
      // ever resolved. redeemForCompany on the server is the authoritative
      // check; gating submission on the local lookup would silently drop
      // a genuinely valid code whenever that request hadn't finished yet
      // (e.g. the user pressed Enter before the input blurred).
      const submitPartnerCode = urlPartnerCode || manualPartnerCode.trim();
      const { userId } = await registerAndLogin(
        email,
        password,
        registrationToken,
        {
          accountType,
          ...(submitPartnerCode ? { partnerCode: submitPartnerCode } : {}),
        },
      );
      toast.success(t("auth.register.successToast"));
      navigate(
        accountType === "accountant" ? "/onboarding/accountant" : "/onboarding",
        { replace: true },
      );
    } catch {
      toast.error(t("auth.register.invalidCodeError"));
    } finally {
      setVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <AuthStatusScreen
        state="loading"
        title={t("auth.session.checkingTitle", "Cargando")}
        message={t(
          "auth.session.checkingDetail",
          "Estamos verificando tu sesión.",
        )}
      />
    );
  }
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="fx-auth-stage">
      <div className="lang-switch">
        <LanguageSwitcher />
      </div>

      <div className="fx-auth-card">
        <div className="fx-auth-form-col">
          <div className="fx-auth-form-inner">
          <AuthBrandMark />
          {partnerLookup?.valid && partnerLookup.stages?.[0] ? (
            <div className="fx-auth-partner-banner">
              <span className="icon-circle">
                <BadgePercent aria-hidden />
              </span>
              <span className="text">
                <Trans
                  i18nKey="auth.register.partnerBenefit"
                  values={{
                    partnerName: partnerLookup.name,
                    amount:
                      partnerLookup.stages[0].discountType === "percent"
                        ? `${partnerLookup.stages[0].discountValue}%`
                        : `$${(partnerLookup.stages[0].discountValue / 100).toLocaleString("es-UY", { minimumFractionDigits: 2 })}`,
                    months: partnerLookup.stages[0].durationMonths,
                  }}
                  components={{ strong: <strong /> }}
                />
              </span>
            </div>
          ) : null}
          {step === 1 ? (
            <AudienceToggle
              value={accountType}
              onChange={setAccountType}
            />
          ) : null}
          <span className="fx-auth-eyebrow">
            {step === 2
              ? t("auth.register.eyebrowStep2", "Verificá tu correo")
              : accountType === "accountant"
                ? t(
                    "auth.register.accountant.eyebrow",
                    "Para contadores",
                  )
                : t("auth.register.eyebrow", "Empezás en minutos")}
          </span>
          <h1 className="fx-auth-h1">
            {step === 1 ? (
              accountType === "accountant" ? (
                <>
                  {t("auth.register.accountant.titleLead", "Creá tu")}{" "}
                  <span className="ital">
                    {t(
                      "auth.register.accountant.titleAccent",
                      "cuenta de contador.",
                    )}
                  </span>
                </>
              ) : (
                <>
                  {t("auth.register.titleLead", "Creá tu")}{" "}
                  <span className="ital">
                    {t("auth.register.titleAccent", "cuenta.")}
                  </span>
                </>
              )
            ) : (
              <>
                {t("auth.register.step2TitleLead", "Revisá tu")}{" "}
                <span className="ital">
                  {t("auth.register.step2TitleAccent", "correo.")}
                </span>
              </>
            )}
          </h1>
          <p className="fx-auth-lead">
            {step === 1 && accountType === "accountant" &&
              t(
                "auth.register.accountant.subtitle",
                "Gratis para contadores. Sumás clientes sin techo y los gestionás —solo o con tu estudio— desde un único panel.",
              )}
            {step === 1 && accountType === "business" &&
              t(
                "auth.register.subtitle",
                "Sin tarjeta. Sin compromiso. Empezás emitiendo comprobantes hoy mismo.",
              )}
            {step === 2 && t("auth.register.step3Title")}
          </p>

          {step === 1 && (
            <>

              <div className="fx-auth-form">
                <GoogleSsoButton
                  intent={accountType}
                  label={t(
                    "auth.sso.signUpWithGoogle",
                    "Registrate con Google",
                  )}
                />
                <div className="fx-auth-divider">
                  {t("auth.login.divider", "o bien")}
                </div>
              </div>

              <form
                className="fx-auth-form"
                onSubmit={form.handleSubmit(
                  handleSendCode,
                  formErrorsTracker("registration"),
                )}
              >
              <div className="fx-auth-field">
                <label htmlFor="email">{t("auth.register.email")}</label>
                <input
                  id="email"
                  type="email"
                  placeholder="vos@email.com"
                  autoComplete="email"
                  {...form.register("email")}
                />
                {form.formState.errors.email ? (
                  <p className="fx-auth-error">
                    {form.formState.errors.email.message}
                  </p>
                ) : null}
              </div>

              <div className="fx-auth-field">
                <label htmlFor="password">{t("auth.register.password")}</label>
                <div className="fx-auth-field-wrap">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    {...form.register("password")}
                  />
                  <button
                    type="button"
                    className="show"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword
                        ? t("auth.login.hidePassword", "Ocultar contraseña")
                        : t("auth.login.showPassword", "Mostrar contraseña")
                    }
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
                <div className="fx-auth-rules">
                  <div className={cn("fx-auth-rule", rules.len && "ok")}>
                    <span className="check">{rules.len ? <Check /> : null}</span>
                    {t("auth.register.passwordStrength.minLength")}
                  </div>
                  <div className={cn("fx-auth-rule", rules.case && "ok")}>
                    <span className="check">{rules.case ? <Check /> : null}</span>
                    {t("auth.register.passwordStrength.uppercase")}
                  </div>
                  <div className={cn("fx-auth-rule", rules.num && "ok")}>
                    <span className="check">{rules.num ? <Check /> : null}</span>
                    {t("auth.register.passwordStrength.number")}
                  </div>
                  <div className={cn("fx-auth-rule", rules.spec && "ok")}>
                    <span className="check">{rules.spec ? <Check /> : null}</span>
                    {t("auth.register.passwordStrength.special")}
                  </div>
                </div>
                {form.formState.errors.password ? (
                  <p className="fx-auth-error">
                    {form.formState.errors.password.message}
                  </p>
                ) : null}
              </div>

              <div className="fx-auth-field">
                <label htmlFor="confirmPassword">
                  {t("auth.register.confirmPassword")}
                </label>
                <div className="fx-auth-field-wrap">
                  <input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    {...form.register("confirmPassword")}
                  />
                </div>
                {form.formState.errors.confirmPassword ? (
                  <p className="fx-auth-error">
                    {form.formState.errors.confirmPassword.message}
                  </p>
                ) : null}
              </div>

              {!urlPartnerCode ? (
                <div className="fx-auth-field">
                  {!showPartnerCodeInput ? (
                    // A bare text link here was invisible: it read as another
                    // line of copy (--fx-accent-700 is #133024, near-black),
                    // carried no container while every other control is a boxed
                    // field, and sat directly under the ink-900 Turnstile block
                    // that takes the eye. Dashed outline + faint accent tint is
                    // the usual "optional, add something here" affordance and
                    // keeps accent FILLS for the real CTA below.
                    <button
                      type="button"
                      className="fx-auth-partner-cta"
                      onClick={() => setShowPartnerCodeInput(true)}
                    >
                      <span className="label">
                        <BadgePercent aria-hidden />
                        {t("auth.register.havePartnerCode", "¿Tenés un código de partner?")}
                      </span>
                      <span className="add" aria-hidden>
                        {t("auth.register.partnerCodeAdd", "Agregar")}
                      </span>
                    </button>
                  ) : (
                    <>
                      <label htmlFor="partnerCode">
                        {t("auth.register.partnerCodeLabel", "Código de partner")}
                      </label>
                      <input
                        id="partnerCode"
                        type="text"
                        autoComplete="off"
                        placeholder="AJE2026"
                        value={manualPartnerCode}
                        onChange={(e) => setManualPartnerCode(e.target.value)}
                        onBlur={commitManualCode}
                      />
                      {committedManualCode !== "" && !partnerLookupLoading ? (
                        partnerLookupErrored ? (
                          <p className="fx-auth-error">
                            {t(
                              "auth.register.partnerCodeCheckError",
                              "No pudimos verificar el código. Intentá de nuevo.",
                            )}
                          </p>
                        ) : partnerLookup?.valid === false ? (
                          <p className="fx-auth-error">
                            {t(
                              "auth.register.partnerCodeInvalid",
                              "No encontramos ese código.",
                            )}
                          </p>
                        ) : null
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}

              {turnstileEnabled ? (
                turnstileToken ? (
                  <div className="fx-auth-turnstile">
                    <div className="left">
                      <span className="ok-circle"><Check /></span>
                      <span>
                        {t(
                          "auth.register.captchaVerified",
                          "Verificación completada",
                        )}
                      </span>
                    </div>
                    <span className="cf">Cloudflare</span>
                  </div>
                ) : (
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                    options={{ theme: "light" }}
                  />
                )
              ) : null}

              <button
                type="submit"
                className="fx-auth-btn fx-auth-btn-accent"
                disabled={sendingCode}
              >
                {sendingCode ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : (
                  <>
                    {t("auth.register.submit", "Crear cuenta")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>

              <div className="fx-auth-alt" style={{ marginTop: 4 }}>
                {t("auth.register.hasAccountQuestion", "¿Ya tenés cuenta?")}{" "}
                <Link to="/login">
                  {t("auth.register.backToLogin", "Iniciá sesión")}
                  <ArrowRight aria-hidden />
                </Link>
              </div>
              </form>
            </>
          )}

          {step === 2 && (
            <div className="fx-auth-form">
              <p className="fx-auth-lead" style={{ marginBottom: 0 }}>
                <Trans
                  i18nKey="auth.register.emailSentMessage"
                  values={{ email }}
                  components={{
                    strong: (
                      <strong style={{ color: "var(--fx-s-text)", fontWeight: 600 }} />
                    ),
                  }}
                />
              </p>

              <div className="fx-auth-field">
                <label htmlFor="code">
                  {t("auth.register.verificationCodeLabel")}
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(
                      e.target.value.replace(/\D/g, "").slice(0, 6),
                    )
                  }
                />
              </div>

              <button
                type="button"
                className="fx-auth-btn fx-auth-btn-accent"
                onClick={handleVerifyAndRegister}
                disabled={verifying}
              >
                {verifying ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : (
                  <>
                    {t("auth.register.verifyButton")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>

              <button
                type="button"
                className="fx-auth-btn fx-auth-btn-ghost"
                onClick={() => {
                  setTurnstileToken(null);
                  setStep(1);
                }}
              >
                {t("pages.common.back")}
              </button>
            </div>
          )}

          <div className="fx-auth-foot">
            <Trans
              i18nKey="auth.register.legalAgreement"
              components={{
                terms: (
                  <a
                    href={getLandingTermsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
                privacy: (
                  <a
                    href={getLandingPrivacyUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </div>
          </div>
        </div>

        <AuthBrandColumn mode="register" audience={accountType} />
      </div>
    </div>
  );
}
